import { Box } from "@mui/material";
import { Dayjs } from "dayjs";
import { clamp } from "hooks/serialisers/numbers";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useTimelineZoom } from "../scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { useManualReviewEvents } from "./contexts/ManualReviewEventContext";

interface ReadOnlySlice {
  start: Dayjs;
  left: number;
  width: number;
}

export const ManualReviewVideoReadOnlyShading = ({
  videoStart,
}: {
  videoStart: Dayjs;
}) => {
  const { writableStart, writableEnd } = useManualReviewEvents();
  const state = useVideoControlState();
  const context = useTimelineZoom();
  const duration = context?.duration ?? state.duration;
  const visibleStart = context?.visibleStart ?? videoStart;
  const visibleEnd = context?.visibleEnd ?? videoStart.add(duration, "second");

  if (
    (writableStart === undefined && writableEnd === undefined) ||
    duration <= 0
  ) {
    return null;
  }

  const toPercent = (time: Dayjs) =>
    clamp((time.diff(visibleStart, "second", true) / duration) * 100, 0, 100);

  const slices: ReadOnlySlice[] = [
    ...(writableStart?.isAfter(visibleStart)
      ? [{ start: visibleStart, end: writableStart }]
      : []),
    ...(writableEnd?.isBefore(visibleEnd)
      ? [{ start: writableEnd, end: visibleEnd }]
      : []),
  ].flatMap(({ start, end }) => {
    const left = toPercent(start);
    const width = toPercent(end) - left;
    return width > 0 ? [{ start, left, width }] : [];
  });

  if (slices.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {slices.map((slice) => (
        <Box
          key={slice.start.valueOf()}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${slice.left}%`,
            width: `${slice.width}%`,
            bgcolor: "grey.500",
            opacity: 0.2,
          }}
        />
      ))}
    </Box>
  );
};
